import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { getFlashBetMatchQuestionStats } from '../Api';
import { FlashBetMatchDto } from '../models/dtos/FlashBetMatchDto';
import FlashBetMatchQuestionsStatsDto from '../models/dtos/FlashBetMatchQuestionsStatsDto';

interface QuestionStatsModalProps {
    open: boolean;
    onClose: () => void;
    match: FlashBetMatchDto | null;
}

const QuestionStatsModal: React.FC<QuestionStatsModalProps> = ({
    open,
    onClose,
    match
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statsData, setStatsData] = useState<FlashBetMatchQuestionsStatsDto[]>([]);

    useEffect(() => {
        if (open && match) {
            fetchQuestionStats();
        }
    }, [open, match]);

    const fetchQuestionStats = async () => {
        if (!match) return;
        
        setLoading(true);
        setError(null);
        try {
            const data = await getFlashBetMatchQuestionStats(match.id);
            setStatsData(data);
        } catch (err) {
            setError('Failed to load question statistics');
            console.error('Error fetching question stats:', err);
        } finally {
            setLoading(false);
        }
    };

    const getPieChartData = (stats: FlashBetMatchQuestionsStatsDto) => {
        return [
            {
                id: 0,
                label: 'Correct Answers',
                value: stats.totalCorrectAnswers,
                color: '#4caf50'
            },
            {
                id: 1,
                label: 'Wrong Answers', 
                value: stats.totalWrongAnswers,
                color: '#f44336'
            }
        ];
    };



    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '70vh' }
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        Question Statistics - {match?.homeTeamDto?.name} vs {match?.awayTeamDto?.name}
                    </Typography>
                </Box>
            </DialogTitle>
            
            <DialogContent dividers>
                {loading && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                )}
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                {!loading && !error && statsData.length === 0 && (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <Typography variant="body1" color="text.secondary">
                            No question statistics available for this match.
                        </Typography>
                    </Box>
                )}
                
                {!loading && !error && statsData.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {statsData.map((questionStats, index) => {
                            const pieData = getPieChartData(questionStats);
                            const totalAnswers = questionStats.totalCorrectAnswers + questionStats.totalWrongAnswers;
                            
                            return (
                                <Card key={index} variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                            {questionStats.question}
                                        </Typography>
                                        
                                        <Box display="flex" gap={3} alignItems="flex-start">
                                            {/* Left side - Answer statistics */}
                                            <Box flex={1}>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                                                    Answer Distribution:
                                                </Typography>
                                                
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {questionStats.answers.map((answer, answerIndex) => (
                                                        <Box 
                                                            key={answerIndex}
                                                            sx={{ 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'space-between',
                                                                p: 1.5,
                                                                border: '1px solid',
                                                                borderColor: answer.correct ? '#4caf50' : '#f44336',
                                                                borderRadius: 2,
                                                                backgroundColor: answer.correct 
                                                                    ? 'rgba(76, 175, 80, 0.08)' 
                                                                    : 'rgba(244, 67, 54, 0.08)'
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Chip
                                                                    label={answer.correct ? "✓ Correct" : "✗ Wrong"}
                                                                    size="small"
                                                                    color={answer.correct ? "success" : "error"}
                                                                    variant="filled"
                                                                />
                                                                <Typography variant="body2">
                                                                    {answer.answerText}
                                                                </Typography>
                                                            </Box>
                                                            <Typography 
                                                                variant="body2" 
                                                                sx={{ 
                                                                    fontWeight: 'bold',
                                                                    color: answer.correct ? '#2e7d32' : '#d32f2f'
                                                                }}
                                                            >
                                                                {answer.totalAnswers} answers
                                                                {totalAnswers > 0 && (
                                                                    <span style={{ marginLeft: '8px', fontSize: '0.8em' }}>
                                                                        ({Math.round((answer.totalAnswers / totalAnswers) * 100)}%)
                                                                    </span>
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                            
                                            {/* Right side - Pie chart */}
                                            <Box sx={{ width: 300, height: 250 }}>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                                    Correct vs Wrong Answers
                                                </Typography>
                                                
                                                {totalAnswers > 0 ? (
                                                    <PieChart
                                                        series={[
                                                            {
                                                                data: pieData,
                                                                innerRadius: 30,
                                                                outerRadius: 80,
                                                                paddingAngle: 2,
                                                                cornerRadius: 5,
                                                            }
                                                        ]}
                                                        width={300}
                                                        height={200}
                                                    />
                                                ) : (
                                                    <Box 
                                                        display="flex" 
                                                        justifyContent="center" 
                                                        alignItems="center" 
                                                        height="100%"
                                                    >
                                                        <Typography variant="body2" color="text.secondary">
                                                            No answers yet
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        
                                        {/* Summary statistics */}
                                        <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.04)', borderRadius: 2 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Total Answers:</strong> {totalAnswers} | 
                                                <strong style={{ color: '#2e7d32', marginLeft: '8px' }}>Correct:</strong> {questionStats.totalCorrectAnswers} | 
                                                <strong style={{ color: '#d32f2f', marginLeft: '8px' }}>Wrong:</strong> {questionStats.totalWrongAnswers}
                                                {totalAnswers > 0 && (
                                                    <>
                                                        {' | '}
                                                        <strong>Accuracy:</strong> {Math.round((questionStats.totalCorrectAnswers / totalAnswers) * 100)}%
                                                    </>
                                                )}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default QuestionStatsModal;